import { useEffect, useRef } from 'react';
import { sendUiSettingsOnUnload, useUpdateUiSettings } from '@/entities/profile';
import { useSettingsStore } from '@/entities/settings';
import { SettingStore, SettingStoreUserValues, UiSettings } from '@/entities/settings/models/types';

const DEBOUNCE_DELAY_MS = 500;

/**
 * Хук синхронизации настроек интерфейса пользователя с бэкендом.
 *
 * Следит за изменениями пользовательских настроек в сторе и отправляет их
 * на сервер через PUT /profile/ui-settings с дебаунсом 500 мс.
 * Первоначальная инициализация (при монтировании) пропускается — сохраняются
 * только изменения, сделанные пользователем.
 *
 * Настройка, сделанная перед самым уходом со страницы, не теряется: отложенная запись
 * отправляется немедленно, как только страница скрывается или выгружается (перезагрузка,
 * закрытие вкладки, переход по внешней ссылке). Иначе таблица, чей вид или отбор поменяли
 * прямо перед перезагрузкой, возвращалась бы к прежнему состоянию.
 *
 * Так же досылается и запись, уже отправленная, но ещё не подтверждённая сервером: выгрузка
 * обрывает её вместе со страницей. Иначе медленная сеть теряла бы настройку, которую отдельно
 * не восстановить, — например, личный дашборд, заведённый взамен уже удалённого совместного.
 */
export const useUiSettingsSync = (userId: string | null) => {
  const { mutate: saveUiSettings } = useUpdateUiSettings();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Предыдущие настройки для определения факта изменения
  const prevSettingsRef = useRef<SettingStoreUserValues | null>(null);
  // Изменение, ожидающее отправки: его же отправляет уход со страницы
  const pendingSettingsRef = useRef<SettingStoreUserValues | null>(null);
  // Последнее отправленное изменение, на которое сервер ещё не ответил
  const unconfirmedSettingsRef = useRef<SettingStoreUserValues | null>(null);

  useEffect(() => {
    if (!userId) return;

    // Снимаем baseline до подписки — это уже инициализированные настройки пользователя
    // (initUserSettings выполняется до рендера children, т.е. до этого эффекта).
    // Любое последующее изменение стора означает действие пользователя.
    prevSettingsRef.current = useSettingsStore.getState().settings[userId] ?? null;

    // Подписываемся на изменения настроек конкретного пользователя в сторе
    const unsubscribe = useSettingsStore.subscribe((state: SettingStore) => {
      const settings = state.settings[userId] ?? null;

      // Пропускаем если настройки не изменились (ссылочное сравнение)
      if (settings === prevSettingsRef.current) return;
      prevSettingsRef.current = settings;

      if (!settings) return;

      // Отменяем предыдущий таймер дебаунса
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Запускаем сохранение с дебаунсом 500 мс
      pendingSettingsRef.current = settings;
      debounceTimerRef.current = setTimeout(() => {
        pendingSettingsRef.current = null;
        unconfirmedSettingsRef.current = settings;
        saveUiSettings(settings satisfies UiSettings, {
          // Ответ на более раннюю запись не снимает более позднюю, ещё не подтверждённую
          onSettled: () => {
            if (unconfirmedSettingsRef.current === settings) unconfirmedSettingsRef.current = null;
          },
        });
      }, DEBOUNCE_DELAY_MS);
    });

    // Уход со страницы: отложенное или ещё не подтверждённое изменение отправляется сразу, запросом,
    // переживающим выгрузку. Достаточно последнего из них: запись несёт настройки целиком.
    // Слушаем оба события: `pagehide` не приходит, когда вкладку просто скрыли, а `visibilitychange`
    // может не прийти на самой выгрузке.
    const flushOnHide = () => {
      const latest = pendingSettingsRef.current ?? unconfirmedSettingsRef.current;
      if (!latest) return;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingSettingsRef.current = null;
      unconfirmedSettingsRef.current = null;
      sendUiSettingsOnUnload(latest satisfies UiSettings);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushOnHide();
    };
    window.addEventListener('pagehide', flushOnHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribe();
      window.removeEventListener('pagehide', flushOnHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Отправленная запись доходит сама: страница остаётся. Держать её нельзя —
      // уход со страницы дослал бы её уже от имени следующего пользователя
      unconfirmedSettingsRef.current = null;
      // Настройка, не успевшая уехать до смены пользователя (выход, перемонтирование дерева),
      // отправляется тем же путём: ждать её больше некому
      const pending = pendingSettingsRef.current;
      if (pending) {
        pendingSettingsRef.current = null;
        sendUiSettingsOnUnload(pending satisfies UiSettings);
      }
    };
  }, [userId, saveUiSettings]);
};
